import { useState } from "react";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Label } from "../../../components/ui/label";
import { Badge } from "../../../components/ui/badge";
import { ScrollArea } from "../../../components/ui/scroll-area";
import { Upload, Grid3X3, FileText, Loader2 } from "lucide-react";
import "../../Page/default_page_style.css";
import { readFileWithEncodingDetection } from "../../../lib/encoding-utils";

export const CrosswordGenerator = () => {
  const [fileContent, setFileContent] = useState("");
  const [grid, setGrid] = useState([]);
  const [gridNumbers, setGridNumbers] = useState([]);
  const [acrossClues, setAcrossClues] = useState([]);
  const [downClues, setDownClues] = useState([]);
  const [puzzleName, setPuzzleName] = useState("");
  const [uploadStatus, setUploadStatus] = useState(null);

  // Formatting function to convert grid and gridNumbers to a single string
  const formatDatabaseString = (grid, gridNumbers) => {
    let letters = "";
    let numbers = "";

    for (let y = 0; y < grid.length; y++) {
      for (let x = 0; x < grid[y].length; x++) {
        if (y === 0 && x === 0) {
          letters = grid[y][x];
          numbers = gridNumbers[y][x];
        } else {
          letters += `,${grid[y][x]}`;
          numbers += `,${gridNumbers[y][x]}`;
        }
      }
    }

    return `${letters}-${numbers}`;
  };

  // Formatting function to convert clues array to a pipe-separated string
  const formatClues = (clues) => {
    return clues.map((clue) => `${clue.number}§${clue.length}§${clue.text}`).join("|");
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        const text = await readFileWithEncodingDetection(file);
        setFileContent(text);
        parseXMLFile(text);
      } catch (error) {
        console.error("File reading error:", error);
      }
    }
  };

  const parseXMLFile = (content) => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(content, "application/xml");

    const gridElement = xmlDoc.querySelector("grid");
    if (!gridElement) {
      console.error("No grid element found in the XML.");
      return;
    }

    const width = Number.parseInt(gridElement.getAttribute("width"), 10);
    const height = Number.parseInt(gridElement.getAttribute("height"), 10);

    const puzzle = Array.from({ length: height }, () => Array(width).fill(" "));
    const puzzleNumbers = Array.from({ length: height }, () => Array(width).fill(null));

    const cluesAcross = [];
    const cluesDown = [];

    const cluesSections = xmlDoc.querySelectorAll("clues");
    cluesSections.forEach((section) => {
      const title = section.querySelector("title");
      if (title) {
        const category = title.textContent.trim().toLowerCase();
        const clues = section.querySelectorAll("clue");
        clues.forEach((clue) => {
          const number = Number.parseInt(clue.getAttribute("number"), 10);
          const length = clue.getAttribute("format");
          const text = clue.textContent.trim();
          if (category === "across" || category === "horizontaal:") {
            cluesAcross.push({ number, text, length });
          } else if (category === "down" || category === "verticaal:") {
            cluesDown.push({ number, text, length });
          }
        });
      }
    });

    const cells = xmlDoc.querySelectorAll("cell");
    cells.forEach((cell) => {
      const x = Number.parseInt(cell.getAttribute("x"), 10) - 1;
      const y = Number.parseInt(cell.getAttribute("y"), 10) - 1;
      const cellType = cell.getAttribute("type");
      const solution = cell.getAttribute("solution");
      const number = cell.getAttribute("number");

      if (cellType === "block") {
        puzzle[y][x] = "#";
        puzzleNumbers[y][x] = null;
      } else if (solution) {
        puzzle[y][x] = solution;
        if (number) {
          const numberInt = Number.parseInt(number, 10);
          puzzleNumbers[y][x] = numberInt < 10 ? `${number}` : number;
        } else {
          puzzleNumbers[y][x] = null;
        }
      }
    });

    setGrid(puzzle);
    setGridNumbers(puzzleNumbers);
    setAcrossClues(cluesAcross);
    setDownClues(cluesDown);
  };

  const handlePostCrossword = async () => {
    if (!puzzleName.trim()) {
      setUploadStatus({ type: "error", message: "Please enter a puzzle name." });
      return;
    }

    if (grid.length === 0) {
      setUploadStatus({ type: "error", message: "No puzzle to upload. Please upload and parse an XML file first." });
      return;
    }

    setUploadStatus({ type: "loading", message: "Uploading puzzle..." });

    const database_string = formatDatabaseString(grid, gridNumbers);
    const down_clues_string = formatClues(downClues);
    const across_clues_string = formatClues(acrossClues);

    const challengeVariants = [
      { variant_id: 1, variant_name: "data", data: database_string },
      { variant_id: 2, variant_name: "down", data: down_clues_string },
      { variant_id: 3, variant_name: "across", data: across_clues_string },
    ];

    try {
      for (const variant of challengeVariants) {
        await handleUpload(17, puzzleName, variant.variant_id, variant.variant_name, variant.data);
      }

      setUploadStatus({ type: "success", message: "Puzzle uploaded successfully!" });
      setPuzzleName("");
    } catch (error) {
      setUploadStatus({ type: "error", message: error.message });
    }
  };

  const handleUpload = async (game_id, challenge_name, variant_id, variant_name, data) => {
    const url = process.env.REACT_APP_API_URL_CHALLENGES;
    const method = "addChallenge";
    const body = {
      method: method,
      game: game_id,
      variant_id: variant_id,
      challenge_name: challenge_name,
      variant_name: variant_name,
      data: data,
    };

    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
      credentials: "include",
    };

    try {
      const response = await fetch(url, options);
      const json = await response.json();
      if (!response.ok) {
        throw new Error(json.message || "Unknown error occurred");
      }
      return json;
    } catch (error) {
      console.error("There was a problem with the fetch operation:", error);
      throw error;
    }
  };

  return (
    <div className=" p-6 w-screen !max-w-[85vw] md:max-w-full">
      <div className=" space-y-8">
        {/* Upload Section */}
        <Card className="border-0">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-xl">
              <Upload className="w-5 h-5" />
              Upload Crossword File
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-primary/50 transition-colors">
                <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <Label htmlFor="xmlFile" className="cursor-pointer">
                  <span className="text-lg font-medium text-slate-700">Choose XML file</span>
                  <p className="text-sm text-slate-500 mt-1">Select your crossword XML file to get started</p>
                </Label>
                <Input id="xmlFile" type="file" accept=".xml" onChange={handleFileUpload} className="hidden" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Content Grid */}
        {grid.length > 0 && (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Crossword Grid */}
            <div className="lg:col-span-2">
              <Card className="border-0 bg-white/80 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Grid3X3 className="w-5 h-5" />
                    Crossword Grid
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[600px] w-full">
                    <div className="inline-block border-2 border-slate-300 rounded-lg overflow-hidden shadow-inner bg-white">
                      {grid.map((row, rowIndex) => (
                        <div key={rowIndex} className="flex">
                          {row.map((cell, cellIndex) => {
                            const isBlackCell = cell === "#";

                            return (
                              <div
                                key={cellIndex}
                                className={`
                                  w-8 h-8 border border-slate-300 flex items-center justify-center relative text-xs font-mono
                                  ${isBlackCell ? "bg-slate-900" : "bg-white hover:bg-slate-50 transition-colors"}
                                `}
                              >
                                {gridNumbers[rowIndex][cellIndex] && !isBlackCell && (
                                  <span className="absolute top-0.5 left-0.5 text-[8px] font-bold text-slate-600 leading-none">{gridNumbers[rowIndex][cellIndex]}</span>
                                )}
                                {!isBlackCell && <span className="text-sm font-bold text-slate-800 mt-1">{cell !== " " ? cell : ""}</span>}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>

            {/* Clues Section */}
            <div className="space-y-6">
              {/* Across Clues */}
              <Card className=" border-0 bg-white/80 backdrop-blur-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <FileText className="w-4 h-4" />
                    Across
                    <Badge variant="secondary" className="ml-auto">
                      {acrossClues.length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-64">
                    <div className="space-y-3">
                      {acrossClues.map((clue, index) => (
                        <div key={index} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                          <div className="flex items-start gap-2">
                            <Badge variant="outline" className="shrink-0 font-mono">
                              {clue.number}
                            </Badge>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-slate-700 leading-relaxed">{clue.text}</p>
                              {clue.length && <p className="text-xs text-slate-500 mt-1">Length: {clue.length}</p>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Down Clues */}
              <Card className=" border-0 bg-white/80 backdrop-blur-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <FileText className="w-4 h-4" />
                    Down
                    <Badge variant="secondary" className="ml-auto">
                      {downClues.length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-64">
                    <div className="space-y-3">
                      {downClues.map((clue, index) => (
                        <div key={index} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                          <div className="flex items-start gap-2">
                            <Badge variant="outline" className="shrink-0 font-mono">
                              {clue.number}
                            </Badge>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-slate-700 leading-relaxed">{clue.text}</p>
                              {clue.length && <p className="text-xs text-slate-500 mt-1">Length: {clue.length}</p>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Upload Puzzle Section */}
        {grid.length > 0 && (
          <Card className=" border-0 bg-white/80 backdrop-blur-sm max-w-md mx-auto">
            <CardHeader>
              <CardTitle className="text-center">Publish Puzzle</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="puzzleName" className="text-sm font-medium">
                  Puzzle Name
                </Label>
                <Input
                  id="puzzleName"
                  placeholder="Enter a memorable puzzle name"
                  value={puzzleName}
                  onChange={(e) => setPuzzleName(e.target.value)}
                  className="transition-all focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <Button onClick={handlePostCrossword} disabled={!puzzleName.trim() || grid.length === 0 || uploadStatus?.type === "loading"} className="w-full h-11 text-base font-medium">
                {uploadStatus?.type === "loading" ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Puzzle
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
